
export default class SerialPortManager {
  constructor() {
    this.port = null;
    this.reader = null;
    this.writer = null;
  }

  setPort(port) {
    this.port = port;
  }

  isOpen() {
    return Boolean(this.port && (this.port.readable || this.port.writable));
  }

  async getSerialPorts() {
    if (!("serial" in navigator)) {
      console.error("Web Serial API not supported.");
      return [];
    }
    const ports = await navigator.serial.getPorts();
    if (!ports.length) return [];

    const filteredPorts = ports.filter(port => {
      const info = port.getInfo();
      return info.usbProductId !== undefined || info.usbVendorId !== undefined;
    });

    return filteredPorts.length ? filteredPorts : ports;
  }


  async requestPort() {
    if (!("serial" in navigator)) {
      throw new Error("Web Serial API is only available in Chrome or Edge. Open this page in Chrome.");
    }
    this.port = await navigator.serial.requestPort();
    return this.port;
  }

  async openPort(options = {}) {
    if (!this.port) {
      throw new Error("No port selected. Please request a port first.");
    }

    const openOptions = {
      baudRate: 115200,
      bufferSize: 4096,
      dataBits: 8,
      stopBits: 1,
      parity: "none",
      flowControl: "none",
      ...options,
    };

    if (!this.port.readable) {
      await this.port.open(openOptions);
      if (typeof this.port.setSignals === "function") {
        try {
          await this.port.setSignals({ dataTerminalReady: true, requestToSend: true });
        } catch {
          /* some adapters do not support control signals */
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 400));
    }

    if (this.port.readable && !this.reader) {
      this.reader = this.port.readable.getReader();
    }
    if (this.port.writable && !this.writer) {
      this.writer = this.port.writable.getWriter();
    }
  }

  async resetReader() {
    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch {
        /* already cancelled */
      }
      try {
        this.reader.releaseLock();
      } catch {
        /* lock already released */
      }
      this.reader = null;
    }
    if (this.port?.readable) {
      this.reader = this.port.readable.getReader();
    }
  }

  async closePort() {
    const reader = this.reader;
    const writer = this.writer;
    const port = this.port;
    this.reader = null;
    this.writer = null;

    if (reader) {
      try {
        await reader.cancel();
      } catch {
        /* already cancelled or port gone */
      }
      try {
        reader.releaseLock();
      } catch {
        /* lock already released */
      }
    }

    if (writer) {
      try {
        writer.releaseLock();
      } catch {
        /* lock already released */
      }
    }

    if (port && (port.readable || port.writable)) {
      try {
        await port.close();
      } catch (error) {
        if (error?.name !== "InvalidStateError") {
          console.error("Error closing port:", error);
        }
      }
    }

    this.port = null;
  }

  async writeData(data) {
    if (!this.writer) {
      throw new Error("No writer available. Is the port open?");
    }
    // Convert the string data to a Uint8Array.
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    await this.writer.write(encodedData);
    // console.log("Data written:", data);
  }

  async writeWithTimeout(data, timeout = 1000) {
    if (!this.writer) {
      return;
      // throw new Error("No writer available. Is the port open?");
    }

    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);

    const writePromise = this.writer.write(encodedData).then(() => {
      // console.log(`Sent: ${data}`);
      // this.writer.releaseLock();
    });

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Write timeout exceeded")), timeout)
    );

    try {
      await Promise.race([writePromise, timeoutPromise]);
    } catch (error) {
      console.error(error.message);
    }
  }

  async readData() {
    if (!this.reader) {
      throw new Error("No reader available. Is the port open?");
    }
    try {
      const { value, done } = await this.reader.read();
      if (done) {
        console.log("Reader finished, port might have been closed.");
        return null;
      }
      const decoder = new TextDecoder();
      const data = decoder.decode(value);
      // console.log("Data read:", data);
      return data;
    } catch (error) {
      console.error("Error reading data:", error);
      throw error;
    }
  }

  async readWithTimeout(timeout = 2000) {
    if (!this.reader) {
      throw new Error("No reader available. Is the port open?");
    }
    const decoder = new TextDecoder();
    let result = "";
    const deadline = Date.now() + timeout;

    while (Date.now() < deadline) {
      const remaining = Math.max(1, deadline - Date.now());
      let timer;
      try {
        const chunk = await Promise.race([
          this.reader.read(),
          new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error("Read timeout exceeded")), remaining);
          }),
        ]);
        if (chunk.done) break;
        result += decoder.decode(chunk.value, { stream: true });
        if (result.includes("Model:") || result.includes("\n")) break;
      } catch (error) {
        if (String(error.message || error).includes("timeout")) {
          await this.resetReader();
          break;
        }
        console.error(error.message || error);
        break;
      } finally {
        clearTimeout(timer);
      }
    }

    return result;
  }

  async readDataByte() {
    if (!this.reader) {
      throw new Error("No reader available. Is the port open?");
    }
    try {
      const { value } = await this.reader.read();
      // if (done) {
      //   console.log("Reader finished, port might have been closed.");
      //   return null;
      // }
      // console.log("Data read:", value);
      return value;
    } catch (error) {
      console.error("Error reading data:", error);
      throw error;
    }
  }


}

